import React, { useLayoutEffect, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { Icons, IconButton, Stack, Box } from "@curves/ui-base";
import usePrintApi from '@apis/usePrintApi';
import useAjax from '@apis/useAjax';
import useMessageDialog from '@util/hook/useMessageDialog';
import MeasureInformationBox from '@icoach/inbody/components/MeasureInformationBox';
import { useSnackbar } from "notistack";

const HeaderBar = React.forwardRef((props, ref) => {
    const {
        pdfUrl,
        onClose: onCloseProps = () => {
        },
        fileName,
        canDownload: canDownloadProps = true,
        canPrint: canPrintProps = true,
        canQrCode: canQrCodeProps = false,
        iframeHTMLRef: iframeHTMLRefProps,
        sourceData: sourceDataProps,
        onFullscreen,
    } = props;

    const Ajax = useAjax();
    const { postPrintApi } = usePrintApi();
    const setMessageDialog = useMessageDialog();
    const { enqueueSnackbar } = useSnackbar();
    const snackbarText = useRef('');
    const iframeContentSize = useRef({ width: 0, height: 0, maxElementWidth: 0 });

    const showSnackbarText = (isShow, text) => {
        if (!isShow) return;
        if (text === snackbarText.current) return;

        enqueueSnackbar(text, {
            variant: "default",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 1000
        });

        snackbarText.current = text;
    }

    const getTransformScale = () => {
        const iframe = iframeHTMLRefProps.current;
        const bodyStyle = iframe.contentWindow.document.body.style.transform;

        if (bodyStyle.includes("scale(")) {
            const match = bodyStyle.match(/scale\(([^)]+)\)/);
            if (match && match[1]) {
                return parseFloat(match[1]);
            }
        }
        return 1;
    }

    const initFrameFlexContent = () => {
        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {
            const bodyElement = iframe.contentWindow.document.body;
            iframeContentSize.current.width = bodyElement.offsetWidth;
            iframeContentSize.current.height = bodyElement.offsetHeight;

            const wrapper = document.createElement('div');

            wrapper.style.display = 'flex';
            wrapper.style.flexDirection = 'column';
            wrapper.style.alignItems = 'center';

            const children = Array.from(bodyElement.children);

            let elementWidth = 0;

            for (let dom of children) {

                if (dom.offsetWidth > elementWidth)
                    elementWidth = dom.offsetWidth;

                dom.style.boxShadow = '2px 2px 8px 0 rgba(0, 0, 0, 0.2)';

                wrapper.appendChild(dom);
            }

            bodyElement.appendChild(wrapper);

            iframeContentSize.current.maxElementWidth = elementWidth;
        }
    }

    const handleFixToOriginal = (isShow = true) => {
        setFrameScale(1);
        showSnackbarText(isShow, "原始大小");
    }
    const handleFitToWidth = (isShow = true) => {
        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {

            const iframeW = iframe.offsetWidth;
            const contentW = iframeContentSize.current.maxElementWidth;
            
            let scale = ((iframeW / contentW) - 0.1).toFixed(1);

            if (scale > 3) scale = 3;
            if (scale < 0.1) scale = 0.1;
            setFrameScale(scale);
            showSnackbarText(isShow, "頁面寬度大小");
        }
    }
    const handleFitToContainer = (isShow = true) => {
        setFrameScale(1);
        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {

            let scale = 1;
            const contentW = iframeContentSize.current.width;
            const contentH = iframeContentSize.current.height;

            //以高度為主
            const iframeH = iframe.offsetHeight;
            const scaleByH = (iframeH / contentH);

            //以寬度為主
            const iframeW = iframe.offsetWidth;
            const scaleByW = (iframeW / contentW);

            scale = (scaleByH < scaleByW) ? scaleByH : scaleByW;

            if (scale > 3) scale = 3;
            if (scale < 0.1) scale = 0.1;
            setFrameScale(scale);
            showSnackbarText(isShow, "適當大小");
        }
    }

    const handleZoonIn = (isShow = true) => {

        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {
            let scale = getTransformScale() + 0.1;

            if (scale > 3) scale = 3;
            setFrameScale(scale);

            const text = scale === 3 ? '縮放至最大' : '顯示放大';
            showSnackbarText(isShow, text);
        }
    }
    const handleZoonOut = (isShow = true) => {
        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {
            let scale = getTransformScale() - 0.1;

            if (scale < 0.1) scale = 0.1;
            setFrameScale(scale);

            const text = scale === 0.1 ? '縮放至最小' : '顯示縮小';
            showSnackbarText(isShow, text);
        }
    }

    const setFrameScale = (scale) => {
        const iframe = iframeHTMLRefProps.current;

        if (iframe && iframe.contentWindow) {
            const bodyElement = iframe.contentWindow.document.body;
            const wrapper = bodyElement.children[0];

            bodyElement.style.transform = `scale(${scale})`;
            bodyElement.style.transformOrigin = 'left top';

            const iframeW = iframe.offsetWidth;
            const elementW = iframeContentSize.current.maxElementWidth;
            const contentH = iframeContentSize.current.height;

            const scaleH = bodyElement.scrollHeight * scale;

            const newW = (iframeW / scale) > elementW ? ((iframeW / scale) - 10) : (elementW + 10);

            bodyElement.style.width = `${newW}px`;
            wrapper.style.width = `${newW}px`;

            if (scale < 1) {
                bodyElement.style.height = `${scaleH}px`;
            } else {
                bodyElement.style.height = `${contentH}px`;
            }
        }
        
    }

    const handlePrinter = () => {
        setMessageDialog({
            open: true,
            title: '列印PDF',
            msg: '是否要列印？',
            onConfirm: doPrintApi,
        });
    };

    const handleOnDownLoad = () => {
        if (!pdfUrl) {
            alert('尚無pdf路徑可供下載');
            return null;
        }
        doDownloadExcel();
    };

    const doDownloadExcel = async () => {
        await Ajax.getPDF(pdfUrl, '', { fileName: fileName });
    };

    const doPrintApi = async () => {
        if (!pdfUrl) {
            alert('尚無pdf路徑可供下載');
            return null;
        }
        await postPrintApi(pdfUrl);
    };


    useImperativeHandle(
        ref,
        () => ({
            initFrameFlexContent,
            handleFixToOriginal,
            handleFitToWidth,
            handleFitToContainer,
            handleZoonIn,
            handleZoonOut,
        }),
    );

    return (
        <Box
            style={{
                alignItems: 'center',
                backgroundColor: '#eeeeee',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                display: 'flex',
                padding: '4px',
            }}
        >
            <Stack>
                <IconButton onClick={handleFixToOriginal}>
                    <Icons.ResizeOriginal />
                </IconButton>
                <IconButton onClick={handleFitToContainer}>
                    <Icons.ResizeFitToContainer />
                </IconButton>
                <IconButton onClick={handleFitToWidth}>
                    <Icons.ResizeFitToWidth />
                </IconButton>
                <IconButton onClick={handleZoonOut}>
                    <Icons.MagnifyingGlassMinus />
                </IconButton>
                <IconButton onClick={handleZoonIn}>
                    <Icons.MagnifyingGlassPlus />
                </IconButton>
            </Stack>
            <Stack className="ml-auto" spacing={2}>
                <IconButton onClick={onFullscreen}>
                    <Icons.OpenInFull />
                </IconButton>
                {canDownloadProps && (
                    <IconButton onClick={handleOnDownLoad}>
                        <Icons.Download />
                    </IconButton>
                )}
                {canPrintProps && (
                    <IconButton onClick={handlePrinter}>
                        <Icons.Print />
                    </IconButton>
                )}
                {canQrCodeProps && <MeasureInformationBox sourceData={sourceDataProps} />}
                <IconButton onClick={onCloseProps}>
                    <Icons.Close />
                </IconButton>
            </Stack>
        </Box>
    );
});

const HTMLViewPdfComponent = React.forwardRef((props, ref) => {
    const {
        htmlUrl = '',
        pdfUrl = '',
        onClose: onCloseProps,
        fileName,
        canDownload: canDownloadProps = true,
        canPrint: canPrintProps = true,
        canQrCode: canQrCodeProps = false,
        sourceData: sourceDataProps,
    } = props;
    const divFrameRef = useRef(null);
    const iframeHTMLRef = useRef(null);
    const headerRef = useRef(null);

    const [width, setWidth] = useState('100%');

    const handleFrameLoad = () => {
        if (headerRef.current) {
            headerRef.current.initFrameFlexContent();
            headerRef.current.handleFitToWidth(false);
        }
    };

    const observerWidth = (entries) => {
        const { contentRect } = entries[0];
        setWidth(contentRect.height);
    };

    const handleOpenFullscreen = () => {
        const screenRef = divFrameRef.current;
        if (!screenRef) return;

        // 跨瀏覽器支援
        if (screenRef.requestFullscreen) {
            screenRef.requestFullscreen();
        } else if (screenRef.webkitRequestFullscreen) { // Safari
            screenRef.webkitRequestFullscreen();
        } else if (screenRef.mozRequestFullScreen) { // Firefox
            screenRef.mozRequestFullScreen();
        } else if (screenRef.msRequestFullscreen) { // IE/Edge
            screenRef.msRequestFullscreen();
        } else {
            alert('瀏覽器不支援全螢幕');
        }
    };
    const handleExitFullscreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        } else {
            alert('瀏覽器不支援關閉全螢幕');
        }
    }

    const isFullscreen = () => {
        return document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;
    }

    useLayoutEffect(
        () => {
            const detectionDiv = new ResizeObserver(observerWidth);
            detectionDiv.observe(divFrameRef.current);

            return () => {
                detectionDiv.disconnect();
            }
        },
        // eslint-disable-next-line
        []
    );

    useEffect(() => {
            const handleResize = () => {
                if (headerRef.current) {
                    headerRef.current.handleFitToWidth(false);
                }
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        },
        // eslint-disable-next-line
        []
    );

    return (
        <Box
            className="rpv-core-viewer"
            style={{
                border: '1px solid rgba(0, 0, 0, 0.3)',
                height: '100%',
            }}
        >
            <Box
                style={{
                    backgroundColor: '#eeeeee',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <HeaderBar
                    ref={headerRef}
                    pdfUrl={pdfUrl}
                    onClose={onCloseProps}
                    fileName={fileName}
                    canDownload={canDownloadProps}
                    canPrint={canPrintProps}
                    canQrCode={canQrCodeProps}
                    iframeHTMLRef={iframeHTMLRef}
                    sourceData={sourceDataProps}
                    onFullscreen={handleOpenFullscreen}
                />
                <Box
                    style={{
                        flex: 1,
                        overflow: 'hidden',
                        background: '#ffffff',
                    }}
                    ref={divFrameRef}
                >
                    <iframe title={fileName || 'undefined'} ref={iframeHTMLRef} id="htmlView"
                            width={'100%'} height={width} src={htmlUrl} onLoad={handleFrameLoad}></iframe>

                    {isFullscreen() && (
                        <IconButton onClick={handleExitFullscreen}
                                    style={{
                                        position: 'fixed',
                                        bottom: '20px',
                                        right: '20px',
                                        zIndex: 9999,
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                    }}>
                            <Icons.CloseFullscreen />
                        </IconButton>)
                    }
                </Box>
            </Box>
        </Box>
    );
});

export default HTMLViewPdfComponent;
